import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const PAGE = 20;
const endpointFor = (game) => (game === "riftbound" ? "/riftscribe/search" : game === "yugioh" ? "/yugioh/search" : "/tcgdex/search");
const paramsFor = (game, q, offset) => (game === "pokemon" ? { q, lang: "both", offset, limit: PAGE, enrich: false } : { q, offset, limit: PAGE });

// Búsqueda de Publicar (P1/P2/D1): solo el juego activo, debounce 300 ms, cancela la anterior,
// primero lo que ya está en HIT y luego lo de la fuente (TCGdex se enriquece sin reordenar).
export function usePublishSearch(game) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const last = useRef({ q: "", offset: 0 });
  const abort = useRef(null);

  const enrich = async (rows, signal) => {
    const ids = rows.filter((r) => !r.enriched && !r.is_custom).map((r) => r.id);
    if (!ids.length) { setEnriching(false); return; }
    setEnriching(true);
    try {
      const { data } = await api.post("/tcgdex/enrich", { ids }, { signal });
      const upd = data?.rows || {};
      setResults((rs) => rs.map((r) => {
        const u = upd[r.id];
        if (!u) return r;
        const clean = Object.fromEntries(Object.entries(u).filter(([, v]) => v != null));
        return { ...r, ...clean, image_url: r.image_url || clean.image_url, enriched: true };
      }));
    } catch { /* cancelada: quedan las filas como estaban */ } finally { if (!signal.aborted) setEnriching(false); }
  };

  const run = async (term) => {
    const qq = term.trim();
    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setSearching(true); setEnriching(false); setError(null);
    last.current = { q: qq, offset: 0 };
    try {
      let shown = [];
      if (game === "pokemon") {
        const loc = await api.get(endpointFor(game), { params: { ...paramsFor(game, qq, 0), local: true }, signal: ctrl.signal }).catch(() => null);
        shown = loc?.data?.results || [];
        if (shown.length) { setResults(shown); setSearched(true); setSearching(false); setEnriching(true); }
      }
      const { data = {} } = await api.get(endpointFor(game), { params: paramsFor(game, qq, 0), signal: ctrl.signal });
      const full = Array.isArray(data.results) ? data.results : [];
      const byId = new Map(full.map((c) => [c.id, c]));
      const seen = new Set(shown.map((c) => c.id));
      const rows = [...shown.map((c) => ({ ...c, ...(byId.get(c.id) || {}), image_url: c.image_url || byId.get(c.id)?.image_url })), ...full.filter((c) => !seen.has(c.id))];
      setResults(rows);
      last.current.offset = full.length;
      setTotal(typeof data.total === "number" ? data.total : rows.length);
      setHasMore(!!data.has_more);
      setSearched(true);
      setSearching(false);
      if (game === "pokemon" && rows.length) enrich(rows, ctrl.signal); else setEnriching(false);
    } catch (e) {
      if (ctrl.signal.aborted || e?.name === "CanceledError") return;
      setError("No se pudo buscar. Intenta de nuevo.");
      setSearching(false);
    }
  };

  useEffect(() => {
    if (q.trim().length < 2) { return undefined; }
    const t = setTimeout(() => { run(q); }, 300);
    return () => { clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, game]);

  useEffect(() => { api.post("/publish/prewarm", null, { params: { game } }).catch(() => {}); }, [game]);

  const loadMore = async () => {
    if (loadingMore || !hasMore || !last.current.q) return;
    setLoadingMore(true);
    try {
      const { data = {} } = await api.get(endpointFor(game), { params: paramsFor(game, last.current.q, last.current.offset) });
      const rows = Array.isArray(data.results) ? data.results : [];
      setResults((rs) => { const seen = new Set(rs.map((c) => c.id)); return [...rs, ...rows.filter((c) => c.id && !seen.has(c.id))]; });
      last.current.offset += rows.length;
      setHasMore(!!data.has_more);
    } catch { setError("No se pudieron cargar más resultados"); } finally { setLoadingMore(false); }
  };

  const reset = () => { abort.current?.abort(); setQ(""); setResults([]); setSearched(false); setSearching(false); setEnriching(false); setTotal(0); setHasMore(false); };

  return { q, setQ, results, searching, searched, enriching, total, hasMore, loadingMore, loadMore, reset, error, retry: () => run(q) };
}
